import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

/** Glasgow Coma Scale */
function GCS({ value, onChange }) {
  const eye = [
    { v: 4, l: "Apre spontaneamente" }, { v: 3, l: "Alla voce" },
    { v: 2, l: "Al dolore" }, { v: 1, l: "Nessuna" },
  ];
  const verb = [
    { v: 5, l: "Orientato" }, { v: 4, l: "Confuso" }, { v: 3, l: "Parole inappropriate" },
    { v: 2, l: "Suoni incomprensibili" }, { v: 1, l: "Nessuna" },
  ];
  const mot = [
    { v: 6, l: "Esegue ordini" }, { v: 5, l: "Localizza il dolore" }, { v: 4, l: "Retrae" },
    { v: 3, l: "Flessione anomala" }, { v: 2, l: "Estensione anomala" }, { v: 1, l: "Nessuna" },
  ];
  const set = (k, v) => onChange({ ...value, [k]: Number(v), total: (k === "eye" ? Number(v) : value.eye || 0) + (k === "verb" ? Number(v) : value.verb || 0) + (k === "mot" ? Number(v) : value.mot || 0) });
  const total = (value.eye || 0) + (value.verb || 0) + (value.mot || 0);
  return (
    <div className="space-y-4">
      {[{ k: "eye", label: "Apertura occhi", opts: eye }, { k: "verb", label: "Risposta verbale", opts: verb }, { k: "mot", label: "Risposta motoria", opts: mot }].map(({ k, label, opts }) => (
        <div key={k} className="space-y-1.5">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</Label>
          <Select value={value[k] ? String(value[k]) : ""} onValueChange={(v) => set(k, v)}>
            <SelectTrigger data-testid={`gcs-${k}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
            <SelectContent>
              {opts.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.v} · {o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ))}
      <div className="flex items-center justify-between p-3 bg-slate-900 text-white rounded-md">
        <span className="text-sm uppercase tracking-widest">Totale GCS</span>
        <span className="font-mono text-2xl font-semibold" data-testid="gcs-total">{total || "—"}</span>
      </div>
    </div>
  );
}

/** Numeric Rating Scale (NRS) 0-10 */
function NRS({ value, onChange }) {
  return (
    <div className="space-y-3">
      <Label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Intensità dolore (0–10)</Label>
      <div className="grid grid-cols-11 gap-1">
        {Array.from({ length: 11 }).map((_, i) => (
          <button
            type="button"
            key={i}
            data-testid={`nrs-${i}`}
            onClick={() => onChange({ score: i })}
            className={`h-12 rounded-md border font-mono text-sm font-semibold transition-colors ${
              value.score === i ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-100"
            }`}
          >{i}</button>
        ))}
      </div>
      {value.score !== undefined && (
        <div className="text-sm text-slate-600">
          Livello registrato: <span className="font-mono font-semibold">{value.score}/10</span>
        </div>
      )}
    </div>
  );
}

/** Chest Pain Scale — descriptive checklist */
function ChestPain({ value, onChange }) {
  const items = [
    { k: "sudden", l: "Insorgenza improvvisa" },
    { k: "oppressive", l: "Carattere oppressivo/costrittivo" },
    { k: "radiation", l: "Irradiazione (braccio/collo/mandibola)" },
    { k: "diaphoresis", l: "Sudorazione algida" },
    { k: "dyspnea", l: "Associato a dispnea" },
    { k: "syncope", l: "Associato a sincope/lipotimia" },
    { k: "risk_factors", l: "Fattori di rischio cardiovascolare noti" },
  ];
  const toggle = (k) => onChange({ ...value, [k]: !value[k] });
  return (
    <div className="space-y-2">
      {items.map(({ k, l }) => (
        <label key={k} className="flex items-center gap-3 p-3 rounded-md border border-slate-200 bg-white cursor-pointer hover:bg-slate-50">
          <Checkbox checked={!!value[k]} onCheckedChange={() => toggle(k)} data-testid={`chestpain-${k}`} />
          <span className="text-sm">{l}</span>
        </label>
      ))}
    </div>
  );
}

/** Cincinnati Prehospital Stroke Scale */
function CPSS({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const items = [
    { k: "face", l: "Asimmetria facciale" },
    { k: "arm", l: "Deficit motorio braccio" },
    { k: "speech", l: "Alterazione linguaggio" },
  ];
  const positive = items.filter((i) => value[i.k] === "abnormal").length;
  return (
    <div className="space-y-3">
      {items.map(({ k, l }) => (
        <div key={k} className="p-3 bg-white border border-slate-200 rounded-md">
          <div className="text-sm font-medium mb-2">{l}</div>
          <div className="flex gap-2">
            {[
              { v: "normal", label: "Normale" },
              { v: "abnormal", label: "Anormale" },
              { v: "unable", label: "Non valutabile" },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                data-testid={`cpss-${k}-${o.v}`}
                onClick={() => set(k, o.v)}
                className={`px-3 py-2 rounded-md text-xs font-semibold border min-h-[40px] ${
                  value[k] === o.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
                }`}
              >{o.label}</button>
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between p-3 bg-slate-900 text-white rounded-md">
        <span className="text-sm uppercase tracking-widest">Segni positivi</span>
        <span className="font-mono text-2xl font-semibold" data-testid="cpss-positive">{positive}/3</span>
      </div>
    </div>
  );
}

export default function ScalesCard({ scales, onChange }) {
  const set = (k, v) => onChange({ ...scales, [k]: v });
  return (
    <div className="bg-white border border-slate-200 rounded-md p-6">
      <h3 className="font-heading text-lg font-semibold mb-4">Scale cliniche</h3>
      <Tabs defaultValue="gcs">
        <TabsList className="grid grid-cols-4 mb-4">
          <TabsTrigger value="gcs" data-testid="tab-gcs">Glasgow</TabsTrigger>
          <TabsTrigger value="nrs" data-testid="tab-nrs">NRS Dolore</TabsTrigger>
          <TabsTrigger value="chest" data-testid="tab-chest">Chest Pain</TabsTrigger>
          <TabsTrigger value="cpss" data-testid="tab-cpss">CPSS</TabsTrigger>
        </TabsList>
        <TabsContent value="gcs"><GCS value={scales.gcs || {}} onChange={(v) => set("gcs", v)} /></TabsContent>
        <TabsContent value="nrs"><NRS value={scales.nrs || {}} onChange={(v) => set("nrs", v)} /></TabsContent>
        <TabsContent value="chest"><ChestPain value={scales.chest_pain || {}} onChange={(v) => set("chest_pain", v)} /></TabsContent>
        <TabsContent value="cpss"><CPSS value={scales.cpss || {}} onChange={(v) => set("cpss", v)} /></TabsContent>
      </Tabs>
    </div>
  );
}
